import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import DeleteConfirmationModal from '@components/DeleteConfirmationModal';
import NoItemFound from '@components/NoItemFound';
import AddItemModal from '@components/rangePlan/AddItemModal';
import { FabricCategory, FitTypePopup, GSMPopup, PriceRange } from '@components/rangePlan/CommonPopups';
import CompositionPopup from '@components/rangePlan/CompositionPopup';
import {
	calculateStylesCount,
	checkValidId,
	getBackgroundColor,
	getFabricTypeWiseParentGroupCount,
	getRangeValue,
	getReducedPrice,
	groupByField,
	hasUserEditPermission,
	isAllowedToDelete,
	isCellIdMatched
} from '@components/rangePlan/utils';
import { ENTITY_TYPE, TABLE_VIEW_TYPES, UPDATE_ENTITY_TYPE } from '@components/rangePlan/constants';
import ScrollButtons from '@components/ScrollButtons';

import { clearGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { setActiveFavouritePageTab } from '@store/favourite/actions';

import {
	downloadRangePlan,
	fetchRangePlanTableData,
	removeRangePlanItem,
	toggleTarget,
	updateRangePlaningTarget
} from '@services/rangePlanService';

import { checkUserType, isAdmin, printApiError } from '@utils/Util';

import ViewDropdown from '@/components/rangePlan/ViewDropdown';
import { useReviewCostingMutation } from '@/api/commonApis';
import RangePlanReadOnlyTable from '@/components/rangePlan/RangePlanReadOnlyTable';
import useClickOutside from '@/hooks/useClickOutside';
import { toastError } from '@/components/Toast';

export const TableActionButton = ({ onActionClick, item, isAllowedToDelete }) => {
	return (
		<div className="edit-items">
			<div className="option">
				<div className="dropdown">
					<button
						className="btn btn-default p-0 dropdown-toggle"
						type="button"
						id={`menu_${item?.id}`}
						data-toggle="dropdown"
						aria-expanded="true"
						disabled={!isAllowedToDelete(item)}
					>
						<CommonImage src="/icons/more_icon.svg" alt="more_icon" isStatic={true} />
					</button>

					<ul
						className="dropdown-menu dropdown-menu-right shadow-lg"
						role="menu"
						aria-labelledby={`menu_${item?.id}`}
						x-placement="bottom-end"
						x-out-of-boundaries
					>
						<li role="presentation" onClick={() => onActionClick(item)}>
							<span className="red">Delete</span>
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
};

export const ToggleTooltip = ({ items, isArray = false, property = 'name' }) => {
	const tagDetails = isArray ? items : Object.keys(items || {});
	return (
		<div class="tag-tooltip">
			<div class="all-status d-flex flex-column">
				{tagDetails?.slice(2)?.map((item, index) => (
					<span class="status radius" key={`tag_${index}`}>
						{isArray ? item?.[property] : `${item} (${items[item]})`}
					</span>
				))}
			</div>
		</div>
	);
};

const RangePlanTable = ({
	entityType = ENTITY_TYPE.FAVOURITE_FOLDER,
	setActiveTab,
	brandName = '',
	brandId = null
}) => {
	const [selectedTargetPriceRangeId, setSelectedTargetPriceRangeId] = useState(null);
	const [selectedTargetGSMId, setSelectedTargetGSMId] = useState(null);
	const [selectedTargetFitTypeId, setSelectedTargetFitTypeId] = useState(null);
	const [selectedTargetCategoryId, setSelectedTargetCategoryId] = useState(null);
	const [selectedTargetCompositionRowId, setSelectedTargetCompositionRowId] = useState(null);
	const [targetQuantityRowId, setTargetQuantityRowId] = useState(null);
	const [isShowTarget, setIsShowTarget] = useState(true);
	const [isShowAchieved, setIsShowAchieved] = useState(true);
	const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
	const dispatch = useDispatch();
	const [itemToDelete, setItemToDelete] = useState(null);
	const [hoverItemId, setHoverItemId] = useState(null);
	const [hoverColumnName, setHoverColumnName] = useState('');
	const { id } = useParams();
	const [targetCostedStyles, setTargetCostedStyles] = useState(null);
	const [rangePlanList, setRangePlanList] = useState([]);
	const [isShowAddItemModal, setIsShowAddItemModal] = useState(false);
	const scrollButtonRef = useRef(null);
	const [targetQuantity, setTargetQuantity] = useState('');
	const [isShowViewDropdown, setIsShowViewDropdown] = useState(false);
	const [tableDetailsView, setTableDetailsView] = useState(true);
	const [tableSummaryView, setTableSummaryView] = useState(false);
	const [selectedRemarks, setSelectedRemarks] = useState(null);
	const [remarksInputValue, setRemarksInputValue] = useState('');

	const viewDropdownRef = useRef();
	const remarksRef = useRef();

	const handleOutsideViewDropdownClick = () => {
		setIsShowViewDropdown(false);
	};

	useClickOutside(viewDropdownRef, handleOutsideViewDropdownClick);

	const productGroupParentCategories = {};
	rangePlanList.forEach((item) => {
		const key = `${item.productGroupResponse?.id}_${item.fabricType?.constant || item?.fabricType?.name}`;

		if (!productGroupParentCategories[key]) {
			productGroupParentCategories[key] = new Set();
		}
		if (item.parentCategoryId) {
			productGroupParentCategories[key].add(item.parentCategoryId);
		}
	});

	const [reviewCosting] = useReviewCostingMutation();

	// Table Header target, techpack, sampleDeveloped, costed
	const calculateRangePlanTableData = () => {
		let targetStyles = 0;
		let achievedStyles = 0;
		let inRangeCostedStyles = 0;
		let totalCostedStyles = 0;

		rangePlanList?.forEach((item) => {
			targetStyles += Number(item?.targetQuantity || 0);
			achievedStyles += Number(item?.achievedQuantity || 0);
			inRangeCostedStyles += Number(item?.achievedBasePriceQuantity || 0);
		});
		setTargetCostedStyles({
			targetStyles,
			achievedStyles,
			inRangeCostedStyles,
			totalCostedStyles
		});
	};

	const getRangePlanTableData = async () => {
		try {
			dispatch(openLoader());
			const response = await fetchRangePlanTableData(id, entityType);
			const data =
				response?.planningItemList?.map((item, index) => {
					const baseItem = {
						...item,
						id: item?.id || `INDEX_${index}`
					};

					if (item?.isNewParentCategory) {
						baseItem.parentCategoryId = `PARENT_${index}`;
					}
					if (item?.isNewFabricType) {
						baseItem.fabricTypeId = `FABRIC_TYPE_${index}`;
					}
					if (item?.isNewProductGroup) {
						baseItem.mainProductGroupId = `PRODUCT_GROUP_${index}`;
					}

					return baseItem;
				}) || [];

			setRangePlanList(data);
			setIsShowTarget(response?.showTargetInfo);
			setIsShowAchieved(response?.showAchievedInfo);
			setTableDetailsView(response?.tableViewType !== TABLE_VIEW_TYPES.SUMMARY_VIEW);
			setTableSummaryView(response?.tableViewType === TABLE_VIEW_TYPES.SUMMARY_VIEW);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		getRangePlanTableData();
	}, [id, entityType]);

	useEffect(() => {
		calculateRangePlanTableData();
	}, [rangePlanList]);

	const showConfirmationModal = (selectedItem) => {
		setIsShowConfirmationModal(true);
		setItemToDelete(selectedItem);
	};

	const handleSelectTarget = async () => {
		// Prevent unchecking if Achieved is already unchecked
		if (isShowTarget && !isShowAchieved) {
			return;
		}

		setIsShowTarget(!isShowTarget);
		try {
			dispatch(openLoader());
			await toggleTarget(id, entityType, {
				showTargetInfo: !isShowTarget,
				tableViewType: TABLE_VIEW_TYPES.DETAILS_VIEW,
				showAchievedInfo: isShowAchieved
			});
			getRangePlanTableData();
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSelectAchieved = async () => {
		try {
			// Prevent unchecking if Target is already unchecked
			if (isShowAchieved && !isShowTarget) {
				return;
			}
			dispatch(openLoader());
			await toggleTarget(id, entityType, {
				showTargetInfo: isShowTarget,
				showAchievedInfo: !isShowAchieved,
				tableViewType: TABLE_VIEW_TYPES.DETAILS_VIEW
			});
			getRangePlanTableData();
			setIsShowAchieved(!isShowAchieved);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleTableView = async (viewName) => {
		try {
			if (viewName === TABLE_VIEW_TYPES.DETAILS_VIEW) {
				setTableDetailsView(true);
				setTableSummaryView(false);
				await toggleTarget(id, entityType, {
					showTargetInfo: isShowTarget,
					tableViewType: TABLE_VIEW_TYPES.DETAILS_VIEW,
					showAchievedInfo: isShowAchieved
				});
				getRangePlanTableData();
			} else if (viewName === TABLE_VIEW_TYPES.SUMMARY_VIEW) {
				setTableSummaryView(true);
				setTableDetailsView(false);
				await toggleTarget(id, entityType, {
					showTargetInfo: isShowTarget,
					tableViewType: TABLE_VIEW_TYPES.SUMMARY_VIEW,
					showAchievedInfo: isShowAchieved
				});
				getRangePlanTableData();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleRemoveRow = async () => {
		if (isAllowedToDelete(itemToDelete))
			try {
				dispatch(openLoader());
				const response = await removeRangePlanItem(itemToDelete?.id);
				if (response?.success) {
					setItemToDelete(null);
					setIsShowConfirmationModal(false);
					getRangePlanTableData();
				}
			} catch (error) {
				printApiError(error);
			} finally {
				dispatch(closeLoader());
			}
	};

	const createFilterObject = (fieldName, fieldValue, fieldKey) => {
		return {
			name: fieldName,
			type: 'text',
			key: encodeURIComponent(fieldKey),
			value: fieldValue,
			identifier: fieldName,
			isShowChip: true
		};
	};

	const handleCategoryClick = (item) => {
		dispatch(clearGlobalFilter());
		if (entityType === ENTITY_TYPE.FAVOURITE_FOLDER) {
			dispatch(setActiveFavouritePageTab({ code: 'DESIGN', title: 'Design' }));
		} else if (entityType === ENTITY_TYPE.COLLECTION) {
			setActiveTab && setActiveTab('designs');
		}

		const categoryFilter = createFilterObject(
			entityType === ENTITY_TYPE.FAVOURITE_FOLDER ? 'categoryIdList' : 'collectionCategory',
			item?.categoryResponse?.name,
			item?.categoryResponse?.id
		);
		const marketIdList = createFilterObject(
			'marketIdList',
			item?.productGroupResponse?.name,
			item?.productGroupResponse?.id
		);
		const constructionFilter = createFilterObject(
			'fabricTypeList',
			item?.fabricType?.name,
			item?.fabricType?.constant || ''
		);

		dispatch(updateTextItemInGlobalFilter(categoryFilter));
		dispatch(updateTextItemInGlobalFilter(marketIdList));
		dispatch(updateTextItemInGlobalFilter(constructionFilter));
	};

	const handleUpdateTarget = async (requestBody, onSuccess) => {
		try {
			dispatch(openLoader());
			const response = await updateRangePlaningTarget(requestBody);
			if (response?.success) {
				getRangePlanTableData();
				onSuccess && onSuccess();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	// Update Target Quantity
	const handleUpdateTargetQuantity = (value, item, isKeyPress = false) => {
		const body = {
			id: checkValidId(item),
			entityId: id,
			entityType: entityType,
			fieldName: UPDATE_ENTITY_TYPE.QUANTITY,
			targetQuantity: value,
			productGroupId: item?.productGroupResponse?.id,
			categoryId: item?.categoryResponse?.id,
			fabricType: item?.fabricType?.constant
		};

		if (value) {
			handleUpdateTarget(body, () => {
				setTargetQuantityRowId(null);
				// Find the next row and foucs it to edit.
				const currentIndex = rangePlanList.findIndex((row) => row.id === item.id);
				if (currentIndex < rangePlanList.length - 1 && isKeyPress) {
					const nextItem = rangePlanList[currentIndex + 1];
					setTargetQuantityRowId(nextItem.id);
					setTargetQuantity(nextItem.targetQuantity);
				}
			});
		} else {
			setTargetQuantityRowId(null);
		}
	};

	const handleUpdateRemarks = (value, item) => {
		const requestBody = {
			id: checkValidId(item),
			entityId: id,
			entityType: entityType,
			remarks: value,
			fieldName: UPDATE_ENTITY_TYPE.REMARKS
		};

		if (!requestBody?.id) {
			requestBody.productGroupId = item?.productGroupResponse?.id;
			requestBody.categoryId = item?.categoryResponse?.id;
		}

		handleUpdateTarget(requestBody, () => {
			setRemarksInputValue(null);
			setSelectedRemarks(null);
		});
	};

	const handleDownload = async () => {
		try {
			dispatch(openLoader());
			await downloadRangePlan(id, entityType);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleHover = (itemId, columnName) => {
		setHoverItemId(itemId);
		setHoverColumnName(columnName);
	};

	const handleReviewCosting = () => {
		reviewCosting({ entityId: id, params: { entityType } })
			.unwrap()
			.then(() => {
				getRangePlanTableData();
			})
			.catch((error) => {
				toastError(error?.data?.messate || 'Something went wrong!');
			});
	};

	const renderAchievedTags = (tagsDetails, columnName, itemId, property = 'name') => {
		return (
			<>
				{Object.keys(tagsDetails || {})
					?.slice(0, 2)
					?.map(
						(composition) =>
							composition && (
								<span className="status gray-1 mr-1 mb-1" key={composition}>
									{composition} {`(${tagsDetails[composition]})`}
								</span>
							)
					)}
				{Object.keys(tagsDetails || {})?.length > 2 && (
					<span
						className="status gray-1  mr-1 mb-1 cursor-pointer count"
						onMouseEnter={() => handleHover(itemId, columnName)}
						onMouseLeave={() => handleHover(null, null)}
						key={itemId}
					>
						{Object.keys(tagsDetails || {})?.slice(2)?.length + '+'}

						{hoverItemId === itemId && hoverColumnName === columnName && (
							<ToggleTooltip items={tagsDetails} property={property} />
						)}
					</span>
				)}
			</>
		);
	};

	const renderTargetTags = (tagsDetails = [], itemId, columnName, property = 'name') => {
		return (
			<>
				{tagsDetails?.slice(0, 2)?.map((item) => (
					<span className="status mr-1 mb-1 gray-1 cursor-pointer count" key={item?.id}>
						{columnName === 'composition'
							? `${item?.persantage}% ${item?.fiberName}`
							: item?.[property]}
					</span>
				))}
				{tagsDetails?.length > 2 && (
					<span
						className="status mr-1 mb-1 gray-1 cursor-pointer count"
						onMouseEnter={() => handleHover(itemId, columnName)}
						onMouseLeave={() => handleHover(null, null)}
					>
						{tagsDetails?.length - 2 + '+'}
						{hoverItemId === itemId && hoverColumnName === columnName && (
							<ToggleTooltip items={tagsDetails} isArray={true} property={property} />
						)}
					</span>
				)}
			</>
		);
	};

	const renderTargetComposition = (composition, itemId, columnName) => {
		return (
			<>
				{composition?.slice(0, 2)?.map((items) => (
					<span className="status mr-1 mb-1 gray-1 cursor-pointer count" key={items[0]?.id}>
						{items?.map((item, index) => (
							<React.Fragment key={item?.id}>
								{item?.percentage}% {item?.fiberName}
								{index < items.length - 1 && ' '}
							</React.Fragment>
						))}
					</span>
				))}
				{composition?.length > 2 && (
					<span
						className="status mr-1 mb-1 gray-1 cursor-pointer count"
						onMouseEnter={() => handleHover(itemId, columnName)}
						onMouseLeave={() => handleHover(null, null)}
					>
						{composition?.length - 2 + '+'}
					</span>
				)}
				{hoverItemId === itemId && hoverColumnName === columnName && (
					<React.Fragment>
						<div className="tag-tooltip">
							<div className="all-status d-flex flex-column">
								{composition?.slice(2)?.map((items) => (
									<span className="status radius" key={items[0]?.id}>
										{items?.map((item, index) => (
											<React.Fragment key={item?.id}>
												{item?.percentage}% {item?.fiberName}
												{index < items.length - 1 && ' '}
											</React.Fragment>
										))}
									</span>
								))}
							</div>
						</div>
					</React.Fragment>
				)}
			</>
		);
	};

	return (
		<React.Fragment>
			<div className="top-header range-plan-top-header">
				<div className="filter-container-top filter-container-top-v2 px-0">
					<div className="filters">
						<div className="chip-container">
							<div className="range-list-and-target-toggle d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center align-self-center">
									<ul className="common-list-component">
										<li>
											Target:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												{targetCostedStyles?.targetStyles}&nbsp;styles
											</span>
										</li>
										<li>
											TP:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												&nbsp;{targetCostedStyles?.achievedStyles}&nbsp;styles
											</span>
										</li>
										<li>
											Sample Dev:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												&nbsp;
												{calculateStylesCount(rangePlanList)?.sampleDevelopedStyles}
												&nbsp;styles
											</span>
										</li>
										<li>
											Costed:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												{calculateStylesCount(rangePlanList)?.costedStyles}
												&nbsp;styles
											</span>
										</li>
										<li>
											In:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												{calculateStylesCount(rangePlanList)?.inRangeStyles}
												&nbsp;styles
											</span>
										</li>
										<li className="d-flex align-items-center">
											Out:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												{calculateStylesCount(rangePlanList)?.outOfRangeStyles}
												&nbsp;styles
											</span>
										</li>
										<li className="d-flex align-items-center">
											Consideration:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												{calculateStylesCount(rangePlanList)?.inConsiderationStyles}
												&nbsp;styles
											</span>
										</li>
										<li className="d-flex align-items-center">
											Out of plan TP:&nbsp;
											<span className="gray_dark_01 regular-12 weight-500">
												{calculateStylesCount(rangePlanList)?.notPlannedTpStyles}
												&nbsp;styles
											</span>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<div className="filter-actions favourite-filter-actions">
						{tableDetailsView && (
							<div className="add-new-design">
								<button
									className="button text size32 px-0"
									disabled={!isShowTarget}
									onClick={() => setIsShowAddItemModal(true)}
								>
									<CommonImage src="/icons/add-round.svg" alt="add" isStatic={true} />
									<span className="ml-1">Add Item</span>
								</button>
							</div>
						)}
						<div className="transparent-select dropdown-view-select ml-2" ref={viewDropdownRef}>
							<div className="dropdown">
								<button
									className="border-0 d-flex align-items-center justify-content-between bg-transparent"
									onClick={() => setIsShowViewDropdown((prev) => !prev)}
								>
									<p className="regular-14 gray_dark_01 mr-1">View</p>
									<CommonImage src="/icons/down-arrow-gray-dark-02.svg" alt="arrow" />
								</button>
								{isShowViewDropdown && (
									<ViewDropdown
										onSelectTargetCol={handleSelectTarget}
										onSelectAchievedCol={handleSelectAchieved}
										isShowTarget={isShowTarget}
										isShowAchieved={isShowAchieved}
										onTableView={handleTableView}
										detailsView={tableDetailsView}
										summaryView={tableSummaryView}
									/>
								)}
							</div>
						</div>
						<div className="target-and-download-btn d-flex align-items-center">
							<button
								className="button border-0 outline mx-2 px-0 d-flex align-items-center"
								onClick={handleDownload}
							>
								<CommonImage
									src="/icons/download-dark-20.svg"
									alt="download"
									isStatic={true}
								/>
								<span className="ml-1 regular-14">Download</span>
							</button>
							<div>
								<ScrollButtons
									className="scroll-table-buttons position-static ml-2"
									scrollButtonRef={scrollButtonRef}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			{tableDetailsView && rangePlanList?.length > 0 ? (
				<div className="range-planing-table">
					<table
						className="table with-border hover scroll-x-label scroll-y-label"
						ref={scrollButtonRef}
					>
						<thead>
							<tr>
								<th rowSpan={2} className="market-column">
									Dep.
								</th>
								<th rowSpan={2} className="category-column">
									Category
								</th>

								{/* Quantity section */}
								<th colSpan={(isShowTarget ? 2 : 1) + (isShowAchieved ? 1 : 0)}>
									Techpack & Sample Quantity
								</th>

								{/* Price Range section - always has at least 6 columns */}
								<th colSpan={(isShowTarget ? 1 : 0) + (isShowAchieved ? 1 : 0) + 4}>
									<div className="d-flex justify-content-between align-items-center">
										Production Price Range
										<div className="edit-items">
											<div className="option">
												<div className="dropdown">
													<button
														className="btn btn-default p-0 dropdown-toggle"
														type="button"
														id="menu_Price_Range"
														data-toggle="dropdown"
														aria-expanded="true"
													>
														<CommonImage
															src="/icons/more_icon.svg"
															alt="more_icon"
															isStatic={true}
														/>
													</button>

													<ul
														className="dropdown-menu dropdown-menu-right shadow-lg"
														role="menu"
														aria-labelledby="menu_Price_Range"
														x-placement="bottom-end"
														x-out-of-boundaries
													>
														<li role="presentation" onClick={handleReviewCosting}>
															<span className="">Review Costing</span>
														</li>
													</ul>
												</div>
											</div>
										</div>
									</div>
								</th>

								{/* GSM Range th */}
								{(isShowTarget || isShowAchieved) && (
									<th colSpan={(isShowTarget ? 1 : 0) + (isShowAchieved ? 1 : 0)}>
										GSM Range
									</th>
								)}

								{/* Fit Type  th*/}
								{(isShowTarget || isShowAchieved) && (
									<th colSpan={(isShowTarget ? 1 : 0) + (isShowAchieved ? 1 : 0)}>
										Fit Type
									</th>
								)}

								{/* Fabric Category th */}
								{(isShowTarget || isShowAchieved) && (
									<th colSpan={(isShowTarget ? 1 : 0) + (isShowAchieved ? 1 : 0)}>
										Fabric Category
									</th>
								)}

								{/* Composition th */}
								{(isShowTarget || isShowAchieved) && (
									<th colSpan={(isShowTarget ? 1 : 0) + (isShowAchieved ? 1 : 0)}>
										Composition
									</th>
								)}

								<th rowSpan={2} className="remarks-column">
									Remarks
								</th>
								<th rowSpan={2} className="action-column"></th>
							</tr>
							<tr>
								{/* Quantity subheaders th */}
								{isShowTarget && <th className="target-column quantity-target">Target</th>}
								{isShowAchieved && (
									<th className="achieved-column quantity-achieved">Techpack</th>
								)}
								<th className="achieved-column quantity-achieved">Sample Dev.</th>
								{/* Price Range subheaders th */}
								{isShowTarget && <th className="target-column price-achieved">Target</th>}
								{isShowAchieved && (
									<th className="achieved-column price-achieved">
										<div className="d-flex align-items-center">
											<p>Achieved</p>
											{/* <div className="ml-1 cursor-pointer">
												<CustomTooltip
													text={'In range/Total Costed (Price Range)'}
													position={POSITIONS.TOP}
													arrowCenter={true}
													className="custom-tooltip"
												>
													<CommonImage
														src="/icons/info.svg"
														alt="info"
														isStatic={true}
													/>
												</CustomTooltip>
											</div> */}
										</div>
									</th>
								)}
								<th className="costed-column">Costed</th>
								<th className="in-column">In</th>
								<th className="in-column">Con.</th>
								<th className="out-column">Out</th>
								{/* GSM Range subheaders th */}
								{isShowTarget && <th className="target-column gsm-target">Target</th>}
								{isShowAchieved && <th className="achieved-column gsm-achieved">Achieved</th>}
								{/* Fit Type subheaders th */}
								{isShowTarget && <th className="target-column fit-target">Target</th>}
								{isShowAchieved && <th className="achieved-column fit-achieved">Achieved</th>}
								{/* Fabric Category subheaders th */}
								{isShowTarget && <th className="target-column fabric-target">Target</th>}
								{isShowAchieved && (
									<th className="achieved-column fabric-achieved">Achieved</th>
								)}
								{/* Composition subheaders th */}
								{isShowTarget && <th className="target-column composition-target">Target</th>}
								{isShowAchieved && (
									<th className="achieved-column composition-achieved">Achieved</th>
								)}
							</tr>
						</thead>

						<tbody>
							{rangePlanList.map((item) => (
								<tr key={`ID_${item?.id}`}>
									{/* Market Column */}
									{item?.isNewFabricType && (
										<td
											rowSpan={item?.noOfSameFabricType}
											className="market-column"
											style={{ backgroundColor: getBackgroundColor(item) }}
										>
											<p className="gray_dark_01 semibold-14 d-flex">
												{`${item?.productGroupResponse?.name}-`}
												{item?.fabricType?.name}
											</p>
											<p className="gray_dark_01">
												{getFabricTypeWiseParentGroupCount(rangePlanList)[
													`${item.productGroupResponse?.id}_${item.fabricType?.constant || item.fabricType?.name}`
												] || 0}
											</p>

											<div className="mt-4">
												{Array.from(
													productGroupParentCategories[
														`${item.productGroupResponse?.id}_${item.fabricType?.constant || item.fabricType?.name}`
													] || []
												).map((pcId) => {
													const matchingItem = rangePlanList.find(
														(listItem) => listItem.parentCategoryId === pcId
													);
													return (
														<p key={pcId} className="gray_dark_01">
															{matchingItem?.parentCategory?.name}:{' '}
															{
																groupByField('parentCategory', rangePlanList)[
																	pcId
																]
															}{' '}
														</p>
													);
												})}
											</div>
										</td>
									)}
									{/* Under Category Column */}
									<td className="category-column" onClick={() => handleCategoryClick(item)}>
										<p className="cursor-pointer text-underline">
											{item?.categoryResponse?.name}
										</p>
									</td>
									{/* Under Quantity > Target Column */}
									{isShowTarget && (
										<td
											className="target-and-achieved-column cursor-pointer"
											onClick={() =>
												hasUserEditPermission() && setTargetQuantityRowId(item?.id)
											}
										>
											{isCellIdMatched(targetQuantityRowId, item) ? (
												<span>
													<input
														type="number"
														min={1}
														step={1}
														value={targetQuantity}
														onChange={(e) => setTargetQuantity(e.target.value)}
														name={`targetQuantity-${item?.id}`}
														autoFocus
														onFocus={() =>
															setTargetQuantity(item?.targetQuantity)
														}
														onBlur={(e) => {
															handleUpdateTargetQuantity(
																Math.floor(e.target.value),
																item
															);
															setTargetQuantity('');
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter') {
																handleUpdateTargetQuantity(
																	Math.floor(e.target.value),
																	item,
																	true
																);
																setTargetQuantity('');
															}
														}}
													/>
												</span>
											) : (
												<p>{item?.targetQuantity}</p>
											)}
										</td>
									)}
									{/* Under Quantity > Achieved */}
									{isShowAchieved && <td>{item?.achievedQuantity}</td>}
									{/* Under Quantity > Sample Development */}
									<td>
										<p>{item?.sampleDevelopedQuantity}</p>
									</td>
									{/* Under Price Range > Target */}
									{isShowTarget && (
										<td
											onClick={() =>
												(isAdmin() ||
													checkUserType('ACCOUNT_MANAGER') ||
													checkUserType('BUSINESS_DEVELOPMENT_MANAGER')) &&
												setSelectedTargetPriceRangeId(item?.id)
											}
											className="price-range-column cursor-pointer"
										>
											{isCellIdMatched(selectedTargetPriceRangeId, item) ? (
												<div>
													<p className="gray_dark_01">
														{getRangeValue(
															item?.targetMinPrice || item?.brandMinPrice,
															item?.targetMaxPrice || item?.brandMaxPrice,
															true
														)}
													</p>
													<PriceRange
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
														setSelectedTargetPriceRangeId={
															setSelectedTargetPriceRangeId
														}
														brandName={brandName}
													/>
												</div>
											) : (
												<p className="gray_dark_01">
													{getRangeValue(
														item?.targetMinPrice || item?.brandMinPrice,
														item?.targetMaxPrice ||
															getReducedPrice(item?.brandMaxPrice),
														true
													)}
												</p>
											)}
										</td>
									)}
									{/* Under Price Range > Achived */}
									{isShowAchieved && (
										<td className="price-range-column achieved">
											<p className="gray_dark_01">
												{/* {item?.achievedMinPrice || item?.achievedMaxPrice ? (
													<span>
														{item?.achievedBasePriceQuantity || 0}/
														{item?.totalCostedQuantity || 0}
													</span>
												) : (
													''
												)}{' '} */}
												{item?.achievedMinPrice || item?.achievedMaxPrice ? (
													<span>
														{getRangeValue(
															item?.achievedMinPrice,
															item?.achievedMaxPrice,
															true
														)}
													</span>
												) : (
													''
												)}
											</p>
										</td>
									)}
									<td className="costed-column">{item?.totalCostedQuantity || 0}</td>
									{/* Under Price Range > In */}
									<td className="in-column">{item?.inRangeCount || 0}</td>
									{/* Under Price Range > Consideration */}
									<td className="con-column">{item?.inConsiderationCount || 0}</td>
									{/* Under Price Range > Out */}
									<td className="out-column">{item?.outOfRangeCount || 0}</td>
									{/* Under GSM Range > Target  */}
									{isShowTarget && (
										<td
											onClick={() =>
												hasUserEditPermission() && setSelectedTargetGSMId(item?.id)
											}
											className="price-range-column cursor-pointer"
										>
											{isCellIdMatched(selectedTargetGSMId, item) ? (
												<div>
													<p>
														{getRangeValue(
															item?.targetMinGsm,
															item?.targetMaxGsm
														)}
													</p>
													<GSMPopup
														setSelectedTargetGSMId={setSelectedTargetGSMId}
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
													/>
												</div>
											) : (
												<p>{getRangeValue(item?.targetMinGsm, item?.targetMaxGsm)}</p>
											)}
										</td>
									)}
									{/* Under GSM Range > Achived  */}
									{isShowAchieved && (
										<td className="price-range-column">
											<p>{getRangeValue(item?.achievedMinGsm, item?.achievedMaxGsm)}</p>
										</td>
									)}
									{/* Under Fit Type > Target */}
									{isShowTarget && (
										<td
											className="fit-type-column cursor-pointer"
											onClick={() =>
												hasUserEditPermission() &&
												setSelectedTargetFitTypeId(item?.id)
											}
										>
											{isCellIdMatched(selectedTargetFitTypeId, item) ? (
												<div>
													{renderTargetTags(
														item?.targetFitTypes || [],
														item?.id,
														'fitType',
														'text'
													)}
													<FitTypePopup
														setSelectedTargetFitTypeId={
															setSelectedTargetFitTypeId
														}
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
													/>
												</div>
											) : (
												renderTargetTags(
													item?.targetFitTypes || [],
													item?.id,
													'fitType',
													'text'
												)
											)}
										</td>
									)}
									{/* Under Fit Type > Achived */}
									{isShowAchieved && (
										<td className="fit-type-column">
											{renderAchievedTags(
												item?.achievedFitTypeCountMap,
												item?.id,
												'fitType',
												'text'
											)}
										</td>
									)}
									{/* Under Fabric Category > Target */}
									{isShowTarget && (
										<td
											onClick={() =>
												hasUserEditPermission() &&
												setSelectedTargetCategoryId(item?.id)
											}
											className="fit-type-column fabric-category-column cursor-pointer"
										>
											{isCellIdMatched(selectedTargetCategoryId, item) ? (
												<div>
													{renderTargetTags(
														item?.targetFabricConstructions || [],
														item?.id,
														'fabricCategory',
														'name'
													)}
													<FabricCategory
														setSelectedTargetCategoryId={
															setSelectedTargetCategoryId
														}
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
													/>
												</div>
											) : (
												renderTargetTags(
													item?.targetFabricConstructions || [],
													item?.id,
													'fabricCategory',
													'name'
												)
											)}
										</td>
									)}
									{/* Under Fabric Category > Achived */}
									{isShowAchieved && (
										<td className="fit-type-column fabric-category-column">
											<div className="status-and-count d-flex">
												{renderAchievedTags(
													item?.achievedConstructionCountMap,
													item?.id,
													'fabricCategory',
													'name'
												)}
											</div>
										</td>
									)}
									{/* Under Composition > Target */}
									{isShowTarget && (
										<td
											className="compositon-target-column cursor-pointer"
											onClick={() =>
												hasUserEditPermission() &&
												setSelectedTargetCompositionRowId(item?.id)
											}
										>
											{item?.id === selectedTargetCompositionRowId ? (
												<div>
													{renderTargetComposition(
														item?.targetFabricCompositions,
														item?.id,
														'composition'
													)}
													<CompositionPopup
														id={id}
														item={item}
														setSelectedTargetCompositionRowId={
															setSelectedTargetCompositionRowId
														}
														onUpdateTarget={handleUpdateTarget}
														entityType={entityType}
														handleClosePopup={() => {
															setSelectedTargetCompositionRowId(null);
														}}
													/>
												</div>
											) : (
												renderTargetComposition(
													item?.targetFabricCompositions,
													item?.id,
													'composition'
												)
											)}
										</td>
									)}
									{/* Under Composition > Achived */}
									{isShowAchieved && (
										<td className="compositon-target-column">
											<div className="status-and-count d-flex">
												{renderAchievedTags(
													item?.achievedCompositionDetailsCountMap,
													item?.id,
													'composition',
													'name'
												)}
											</div>
										</td>
									)}
									{/* Remarks Column  */}
									<td
										className="remarks-column"
										onClick={() => hasUserEditPermission() && setSelectedRemarks(item)}
									>
										{selectedRemarks?.id === item?.id ? (
											<textarea
												rows={1}
												ref={remarksRef}
												type="text"
												value={remarksInputValue}
												onChange={(e) => {
													remarksRef.current.style.height = 'auto';
													remarksRef.current.style.height = `${remarksRef.current.scrollHeight}px`;
													setRemarksInputValue(e.target.value);
												}}
												onFocus={(e) => {
													const length = e.target.value.length;
													e.target.setSelectionRange(length, length);
													setRemarksInputValue(item?.remarks);
												}}
												onBlur={() => {
													handleUpdateRemarks(remarksInputValue, item);
												}}
												autoFocus
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault();
														handleUpdateRemarks(remarksInputValue, item);
													}
												}}
												style={{ overflow: 'hidden', height: '100%' }}
											/>
										) : (
											<p>{item?.remarks}</p>
										)}
									</td>
									{/* Under Three Dot Action Button  */}
									<td
										className={`${
											isAllowedToDelete(item)
												? 'action-column '
												: 'action-column ineligible-bg'
										}`}
									>
										<TableActionButton
											item={item}
											onActionClick={showConfirmationModal}
											isAllowedToDelete={isAllowedToDelete}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : tableSummaryView && rangePlanList?.length > 0 ? (
				<div
					className="categories-range-table mt-3 d-flex justify-content-start align-items-start"
					ref={scrollButtonRef}
				>
					<RangePlanReadOnlyTable rangePlanList={rangePlanList} />
				</div>
			) : (
				<NoItemFound
					onClick={() => setIsShowAddItemModal(true)}
					buttonTitle="Add Item"
					title="No items found"
					description="Add items to create your range plan"
				/>
			)}

			{isShowConfirmationModal && (
				<DeleteConfirmationModal
					title="Delete Row"
					description="Are you sure about deleting this Row?"
					onClose={() => setIsShowConfirmationModal(false)}
					onConfirm={handleRemoveRow}
				/>
			)}
			{isShowAddItemModal && (
				<AddItemModal
					onClose={() => setIsShowAddItemModal(false)}
					id={id}
					getRangePlanTableData={getRangePlanTableData}
					entityType={entityType}
					brandId={brandId}
				/>
			)}
		</React.Fragment>
	);
};

export default RangePlanTable;
